import { supabase } from './supabase';
import { Medication } from '../types';

// Konwersja między formatem aplikacji (camelCase) a formatem bazy danych (snake_case)
const toDbFormat = (data: Medication) => ({
  id: data.id,
  date: data.date,
  time: data.time,
  name: data.name,
  dosage: typeof data.dosage === 'string' ? parseInt(data.dosage) || 1 : data.dosage,
  frequency: data.frequency,
  with_meal: data.withMeal || false,
  taken: data.taken,
  notes: data.notes || ''
});

const fromDbFormat = (dbData: any): Medication => ({
  id: dbData.id,
  date: dbData.date,
  time: dbData.time,
  name: dbData.name,
  dosage: typeof dbData.dosage === 'string' ? parseInt(dbData.dosage) || 1 : (dbData.dosage || 1),
  frequency: dbData.frequency,
  withMeal: dbData.with_meal || false,
  taken: dbData.taken,
  notes: dbData.notes || ''
});

export const medicationService = {
  // Pobierz wszystkie leki użytkownika
  getAll: async (): Promise<Medication[]> => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('Użytkownik nie jest zalogowany');
    }

    const { data, error } = await supabase
      .from('medications')
      .select('*')
      .eq('user_id', user.id)
      .order('date', { ascending: false })
      .order('time', { ascending: false });

    if (error) {
      console.error('Błąd pobierania leków:', error);
      throw error;
    }

    return (data || []).map(fromDbFormat);
  },

  // Utwórz nowy lek
  create: async (medication: Medication): Promise<Medication> => {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user) {
      throw new Error('Użytkownik nie jest zalogowany');
    }

    const dbData = {
      ...toDbFormat(medication),
      user_id: user.id
    };

    console.log('Zapisywanie leku do bazy:', dbData);
    
    const { data: insertedData, error } = await supabase
      .from('medications')
      .insert([dbData])
      .select()
      .single();

    if (error) {
      console.error('Błąd tworzenia leku:', error);
      console.error('Szczegóły błędu:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      throw error;
    }
    
    console.log('Lek zapisany pomyślnie:', insertedData);

    return fromDbFormat(insertedData);
  },

  // Zaktualizuj lek
  update: async (id: string, medication: Medication): Promise<Medication> => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('Użytkownik nie jest zalogowany');
    }

    const dbData = toDbFormat(medication);
    const { data: updatedData, error } = await supabase
      .from('medications')
      .update(dbData)
      .eq('id', id)
      .eq('user_id', user.id) // Upewnij się, że użytkownik może edytować tylko swoje leki
      .select()
      .single();

    if (error) {
      console.error('Błąd aktualizacji leku:', error);
      throw error;
    }

    return fromDbFormat(updatedData);
  },

  // Usuń lek
  delete: async (id: string): Promise<void> => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('Użytkownik nie jest zalogowany');
    }

    const { error } = await supabase
      .from('medications')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id); // Upewnij się, że użytkownik może usuwać tylko swoje leki

    if (error) {
      console.error('Błąd usuwania leku:', error);
      throw error;
    }
  }
};
